/**
 * Channel Detail Save RetailStore Config Policy
 *
 * Triggered when retail store config save is initiated.
 * Merges temp values into channel.settings and calls UpdateSalesChannelIntegrationConfiguration.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';

export const createChannelDetailSaveRetailStoreConfigPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channel-detail-save-retailstore-config',

  shouldReact: ( prev, next ) => {
    return !prev.channelDetail.loading.savingRetailStoreConfig && next.channelDetail.loading.savingRetailStoreConfig;
  },

  onStateChange: async ({ next, ctx }) => {
    const { dispatch } = ctx;

    if ( next.channelDetail.mode.type !== 'view' ) return;

    const integrationId = next.channelDetail.mode.integrationId;
    const channel = next.salesChannels[integrationId];

    if ( !channel ) return;

    const { isEditingWarehouses, isEditingPriceLists, tempWarehouseIds, tempDefaultChannelWarehouseId, tempPriceListIds, tempDefaultPriceListId } =
      next.channelDetail.retailStoreConfigEdit;

    const existingSettings = ( channel.settings ?? {}) as Record<string, unknown>;

    // Only override the section(s) that are actively being edited
    const settings = {
      ...existingSettings,
      ...( isEditingWarehouses ? {
        warehouses: tempWarehouseIds,
        defaultChannelWarehouseId: tempDefaultChannelWarehouseId
      } : {}),
      ...( isEditingPriceLists ? {
        priceLists: tempPriceListIds,
        defaultPriceListId: tempDefaultPriceListId
      } : {})
    };

    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- console.error operator log + loading reset only, no user surface (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.UpdateSystemIntegrationConfiguration({
        id: channel.id,
        body: { settings } as any
      });

      // Success - close edit mode for both sections
      dispatch(
        { type: 'integrations/channels/detail/retailStoreConfig/cancel', payload: { section: 'warehouses' }},
        { source: 'policy' }
      );
      dispatch(
        { type: 'integrations/channels/detail/retailStoreConfig/cancel', payload: { section: 'priceLists' }},
        { source: 'policy' }
      );
    } catch ( error ) {
      console.error( 'Error saving retail store config:', error );
    } finally {
      dispatch(
        { type: 'integrations/channels/detail/loading/setSavingRetailStoreConfig', payload: { isLoading: false }},
        { source: 'policy' }
      );
    }
  }
});
